import { useCallback, useState, useRef } from 'react';
import './restore-key-page.scss';
import Authorization from 'modules/authorization';
import { KeyIcon, DocumentIcon, CheckIcon } from 'assets/icons';
import { ReactComponent as WarningIcon } from 'modules/common-icons/warning-icon.svg';
import LostKeyIcon from 'assets/icons/lost-key.svg';
import SaveKeyIcon from 'assets/icons/save-key.svg';
import GiveKeyIcon from 'assets/icons/give-key.svg';
import AppCheckbox from 'modules/app-checkbox/app-checkbox';
import { IconBtn } from '@ru-digital-assets/ui-component';
import { KeyWordModal } from 'components';
import cn from 'classnames';
import { UiSelect } from '@ru-digital-assets/ui-component';
import { setPush } from 'store/actions/app';
import { useHistory } from 'react-router';
import { Wallet } from 'ethers';
import { downloadFile, getPdfFromBase64 } from 'utils/files/downloadFile';
import { showLoader, hideLoader } from 'store/actions/loader';
import companyApi from 'services/api/CompanyApi';
import { useSelector, useDispatch } from 'hooks/redux';
import { createHash } from 'crypto-pro';
import { useSign } from 'hooks';
const aes256 = require('aes256');

enum RestoreKeyProcessState {
  SelectStorageType = 'SelectStorageType',
  Sign = 'Sign',
  Result = 'Result',
}

export default function RestoreKeyPage() {
  const history = useHistory();
  const [riskAgree, setRiskAgree] = useState(false);
  const [storageMode, setStorageMode] = useState<'file' | 'keystrore'>();
  const [keyWordModalOpen, setKeyWordModalOpen] = useState(false);
  const keyWord = useRef<string>();
  const accountId = useSelector((state) => state.auth.account.id);
  const token = useSelector((state) => state.auth.account.token);
  const [processState, setProcessState] = useState<RestoreKeyProcessState>(
    RestoreKeyProcessState.SelectStorageType
  );

  const dispatch = useDispatch();

  const {
    certificatesList,
    selectedCertificate,
    signEntity,
    onUserSelectCertificate,
  } = useSign();

  const onSelectFileStorage = useCallback(() => {
    setKeyWordModalOpen(true);
  }, []);

  const onSubmitKeyWord = useCallback((key?: string) => {
    keyWord.current = key;
    setKeyWordModalOpen(false);
    setStorageMode('file');
  }, []);

  const onGoToSign = useCallback(() => {
    setProcessState(RestoreKeyProcessState.Sign);
  }, []);

  const onSignKey = useCallback(() => {
    const wallet = Wallet.createRandom();
    const encryptedKey = keyWord.current
      ? 'ENC(' + aes256.encrypt(keyWord.current, wallet.privateKey) + ')'
      : wallet.privateKey;
    dispatch(showLoader());
    const signPayload = {
      accountId,
      pubKey: wallet.publicKey,
    };
    createHash(JSON.stringify(signPayload))
      .then((keyHash) => {
        signEntity(keyHash).then(({ pdfSigned: signature }) => {
          if (signature) {
            companyApi
              .keyRecovery(token, {
                pubKey: wallet.publicKey,
                signature,
              })
              .then(() => {
                const resultBlob = getPdfFromBase64(encryptedKey);
                downloadFile(resultBlob, 'Private.txt', 'text/plain');
                setProcessState(RestoreKeyProcessState.Result);
              })
              .catch(() => {
                dispatch(
                  setPush({
                    type: 'error',
                    message: 'Ошибка при отправке подписи ключа',
                  })
                );
              })
              .finally(() => {
                dispatch(hideLoader());
              });
          } else {
            dispatch(hideLoader());
            dispatch(
              setPush({ type: 'error', message: 'Ошибка при подписи ключа' })
            );
          }
        });
      })
      .catch(() => {
        dispatch(hideLoader());
        dispatch(
          setPush({ type: 'error', message: 'Ошибка при создании хеша ключа' })
        );
      });
  }, [signEntity, token, accountId]);

  return (
    <Authorization>
      {processState === RestoreKeyProcessState.SelectStorageType && (
        <>
          <KeyWordModal
            open={keyWordModalOpen}
            onClose={() => {
              setKeyWordModalOpen(false);
            }}
            onSubmit={onSubmitKeyWord}
          />
          <div className="restore-key-page">
            <p className="p p_font-size_32 p_font-weight_light mb_28">
              Восстановление Ключа
            </p>
            <p className="p p_bold p_font-size_18 mb_16">
              Для работы на платформе необходимо заново сгенерировать Ключ
            </p>
            <p className="p mb_24">Выберите способ хранения Ключа:</p>
            <div role="button" className="restore-key-page__storage mb_24">
              <div
                className={cn(
                  'restore-key-page__storage-mode',
                  storageMode === 'file' &&
                    'restore-key-page__storage-mode_selected'
                )}
                onClick={onSelectFileStorage}
              >
                <div className="mr_16">
                  <DocumentIcon />
                </div>
                <div>
                  <p className="p p_bold p_font-size_18">Сохранить файлом</p>
                  <p className="p p_font-size_12">В формате *.key</p>
                </div>
                {storageMode === 'file' && <CheckIcon className="ml_16" />}
              </div>
              <div
                role="button"
                className={cn(
                  'restore-key-page__storage-mode',
                  storageMode === 'keystrore' &&
                    'restore-key-page__storage-mode_selected'
                )}
                onClick={() => {}}
              >
                <div className="mr_16">
                  <KeyIcon />
                </div>
                <p className="p p_bold p_font-size_18">Сохранить в Keystore</p>
                {storageMode === 'keystrore' && <CheckIcon className="ml_16" />}
              </div>
            </div>
            <div className="restore-key-page__key-info mb_36">
              <div className="restore-key-page__key-info-header">
                <WarningIcon className="icon" />
                <p className="p p_bold p_color_orange ml_10">
                  Внимание! Рекомендации по обращению с Ключом:
                </p>
              </div>
              <div className="restore-key-page__key-info-body">
                <div className="restore-key-page__recommendation">
                  <img
                    className="restore-key-page__recommendation-icon"
                    src={LostKeyIcon}
                    alt="Не теряйте Ключ"
                  />
                  <p className="p p_font-size_12 p_color_orange p_text_center">
                    Не теряйте Ключ
                  </p>
                </div>
                <div className="restore-key-page__recommendation">
                  <img
                    className="restore-key-page__recommendation-icon"
                    src={SaveKeyIcon}
                    alt="Не храните Ключ в открытом виде"
                  />
                  <p className="p p_font-size_12 p_color_orange p_text_center">
                    Не храните Ключ
                    <br />в открытом виде
                  </p>
                </div>
                <div className="restore-key-page__recommendation">
                  <img
                    className="restore-key-page__recommendation-icon"
                    src={GiveKeyIcon}
                    alt="Не передавайте Ключ третьим лицам"
                  />
                  <p className="p p_font-size_12 p_color_orange p_text_center">
                    Не передавайте Ключ третьим лицам
                  </p>
                </div>
              </div>
            </div>
            <AppCheckbox
              className="mb_36"
              checked={riskAgree}
              sm
              onChangeValue={(value) => {
                setRiskAgree(value);
              }}
              title="Я ознакомлен и принимаю риск утраты Ключа или несанкционированного доступа третьих лиц к моему Ключу, указанный в пунктах 4.4, 4.5 Декларации о рисках"
            />
            <IconBtn
              lg
              title="Сгенерировать Ключ"
              onClick={onGoToSign}
              disabled={!storageMode || !riskAgree}
            />
          </div>
        </>
      )}
      {processState === RestoreKeyProcessState.Sign && (
        <div className="restore-key-page__sign">
          <p className="p p_font-size_32 p_font-weight_light mb_28">
            Восстановление Ключа
          </p>
          <p className="p p_bold p_font-size_18 mb_16">
            Генерация нового Ключа
          </p>
          <p className="p mb_24">
            Для генерации нового ключа необходимо подписание
            <br />
            усиленной квалифицированной электронной подписью
          </p>
          <div className="mb_32">
            <UiSelect
              placeholder="Выберите подпись"
              value={selectedCertificate}
              options={certificatesList}
              lg
              onChange={onUserSelectCertificate}
              disabled={certificatesList.length === 0}
            />
          </div>
          <IconBtn
            lg
            title="Подписать и сгенерировать Ключ"
            onClick={onSignKey}
            disabled={!selectedCertificate}
          />
        </div>
      )}
      {processState === RestoreKeyProcessState.Result && (
        <div>
          <p className="p p_font-size_32 p_font-weight_light mb_28">
            Восстановление Ключа
          </p>
          <p className="p p_bold p_font-size_18 mb_16">
            Новый Ключ сгенерирован!
          </p>
          <p className="p mb_24">
            Дождитесь подтверждения Оператором
            <br />и внесения изменений в Реестр Пользователей
          </p>
          <IconBtn
            lg
            title="Вернуться к работе на платформе"
            onClick={() => history.push('/')}
          />
        </div>
      )}
    </Authorization>
  );
}
